import type { Dinero } from 'dinero.js'
import type UserCartStore from '../../UserData/_stores/UserCartStore'
import type { BaseItem } from '../../UserData/_stores/UserCartStore'
import { ProductItem } from '../../UserData/_stores/UserCartStore'
import { getItemTrackingId } from './CartTracking'

const HISTORY_CHANGE_EVENT = 'aow:history-change'
const OPPREF_COOKIE = '__oppref'

type OpenAIAdsStandardEventName =
  'page_viewed'
  | 'contents_viewed'
  | 'items_added'
  | 'checkout_started'
  | 'order_created'
  | 'registration_completed'

type OpenAIAdsServerCartEventName = 'checkout_started' | 'payment_info_added'

interface OpenAIAdsEventOptions {
  event_id?: string
  opt_out?: boolean
}

interface OpenAIAdsCustomEventOptions extends OpenAIAdsEventOptions {
  custom_event_name: string
}

interface OpenAIAdsContent {
  id?: string
  name?: string
  content_type?: string
  quantity?: number
  amount?: number
  currency?: string
}

interface OpenAIAdsContentsEventData {
  type: 'contents'
  amount?: number
  currency?: string
  contents?: OpenAIAdsContent[]
}

interface OpenAIAdsCustomerActionEventData {
  type: 'customer_action'
  amount?: number
  currency?: string
}

interface OpenAIAdsCustomEventData extends Omit<OpenAIAdsContentsEventData, 'type'> {
  type: 'custom'
  plan_id?: string
}

type OpenAIAdsEventData = OpenAIAdsContentsEventData | OpenAIAdsCustomerActionEventData

interface OpenAIAdsPixel {
  (command: 'measure', eventName: OpenAIAdsStandardEventName, data: OpenAIAdsEventData, options?: OpenAIAdsEventOptions): void
  (command: 'measure', eventName: 'custom', data: OpenAIAdsCustomEventData, options: OpenAIAdsCustomEventOptions): void
}

declare global {
  interface Window {
    oaiq?: OpenAIAdsPixel
  }
}

let started = false
let currentPagePath = ''

function compactObject<T extends object>(payload: T): T {
  return Object.entries(payload).reduce<Partial<T>>((result, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      result[key as keyof T] = value as T[keyof T]
    }

    return result
  }, {}) as T
}

function hasOptions(options: OpenAIAdsEventOptions | undefined): boolean {
  return options !== undefined && Object.keys(options).length > 0
}

function getCookie(name: string): string | undefined {
  const cookie = document.cookie
    .split('; ')
    .find(value => value.startsWith(`${encodeURIComponent(name)}=`))

  return cookie !== undefined ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : undefined
}

function measureOpenAIAdsEvent(
  eventName: OpenAIAdsStandardEventName,
  data: OpenAIAdsEventData,
  options?: OpenAIAdsEventOptions
): void {
  if (typeof window.oaiq !== 'function') {
    return
  }

  if (hasOptions(options)) {
    window.oaiq('measure', eventName, data, options)
    return
  }

  window.oaiq('measure', eventName, data)
}

function measureOpenAIAdsCustomEvent(
  customEventName: string,
  data: OpenAIAdsCustomEventData,
  options?: OpenAIAdsEventOptions
): void {
  if (typeof window.oaiq !== 'function') {
    return
  }

  window.oaiq('measure', 'custom', data, {
    ...options,
    custom_event_name: customEventName
  })
}

function getItemTrackingName(item: BaseItem): string {
  return item instanceof ProductItem ? item.get('product_name') : item.get('name')
}

function getOpenAIAdsCartContents(cart: UserCartStore): OpenAIAdsContent[] {
  const currency = cart.get('subtotal').get('currency')
  const contents: OpenAIAdsContent[] = []

  cart.get('items').forEach((item) => {
    contents.push(compactObject({
      id: getItemTrackingId(item),
      name: getItemTrackingName(item),
      content_type: 'product',
      quantity: item.get('quantity'),
      amount: item.get('price').get('amount'),
      currency
    }))
  })

  return contents
}

function buildOpenAIAdsCartPayload(cart: UserCartStore): OpenAIAdsContentsEventData {
  const subtotal = cart.get('subtotal')
  const discount = cart.get('discount')

  return compactObject<OpenAIAdsContentsEventData>({
    type: 'contents',
    amount: Math.max(0, subtotal.get('amount') - discount.get('amount')),
    currency: subtotal.get('currency'),
    contents: getOpenAIAdsCartContents(cart)
  })
}

export function trackOpenAIAdsPageView(pagePath = window.location.pathname, pageTitle = document.title): void {
  measureOpenAIAdsEvent('page_viewed', {
    type: 'contents',
    contents: [
      compactObject({
        id: pagePath,
        name: pageTitle,
        content_type: 'page'
      })
    ]
  })
}

export function trackOpenAIAdsItemsAdded(
  price: Dinero,
  contents: OpenAIAdsContent[],
  eventId?: string
): void {
  measureOpenAIAdsEvent('items_added', compactObject<OpenAIAdsContentsEventData>({
    type: 'contents',
    amount: Math.max(0, price.getAmount()),
    currency: price.getCurrency(),
    contents: contents.map(content => compactObject(content))
  }), compactObject({
    event_id: eventId
  }))
}

export function trackOpenAIAdsCheckoutStarted(cart: UserCartStore, eventId?: string): void {
  measureOpenAIAdsEvent('checkout_started', buildOpenAIAdsCartPayload(cart), compactObject({
    event_id: eventId
  }))
}

export function trackOpenAIAdsPaymentInfoAdded(cart: UserCartStore, eventId?: string): void {
  measureOpenAIAdsCustomEvent('payment_info_added', {
    ...buildOpenAIAdsCartPayload(cart),
    type: 'custom'
  }, compactObject({
    event_id: eventId
  }))
}

export function trackOpenAIAdsOrderCreated(cart: UserCartStore, eventId?: string): void {
  measureOpenAIAdsEvent('order_created', buildOpenAIAdsCartPayload(cart), compactObject({
    event_id: eventId
  }))
}

export function trackOpenAIAdsRegistrationCompleted(eventId?: string): void {
  measureOpenAIAdsEvent('registration_completed', {
    type: 'customer_action'
  }, compactObject({
    event_id: eventId
  }))
}

export function queueOpenAIAdsServerEvent(
  eventName: OpenAIAdsServerCartEventName,
  cart: UserCartStore,
  eventId: string
): void {
  const fd = new FormData()
  fd.append('event_name', eventName)
  fd.append('event_id', eventId)
  fd.append('id_cart', String(cart.get('id')))
  fd.append('event_source_url', window.location.href)

  const oppref = getCookie(OPPREF_COOKIE)
  if (oppref !== undefined) {
    fd.append('oppref', oppref)
  }

  if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon('ajax/api/cart/openai-ads-conversion-event', fd)) {
    return
  }

  void fetch('ajax/api/cart/openai-ads-conversion-event', {
    method: 'POST',
    body: fd,
    keepalive: true
  }).catch(() => undefined)
}

function trackCurrentPageView(): void {
  if (window.location.pathname === currentPagePath) {
    return
  }

  currentPagePath = window.location.pathname
  trackOpenAIAdsPageView(currentPagePath)
}

export function startOpenAIAdsPageViewTracking(): void {
  if (started) {
    return
  }

  started = true
  currentPagePath = window.location.pathname

  window.addEventListener('popstate', trackCurrentPageView)
  window.addEventListener(HISTORY_CHANGE_EVENT, trackCurrentPageView)
}
